import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  User,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Stethoscope,
  ArrowLeft,
} from "lucide-react";
import { useAuthStore } from "../store/auth";
const Login = ({ portal }) => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState("");
  const { login, loading } = useAuthStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");

    if (!email || !senha) {
      setErro("Por favor, preencha email e senha");
      return;
    }

    if (senha.length < 6) {
      setErro("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    try {
      await login(email.trim(), senha, portal);
    } catch (error) {
      const errorMessage = error?.message || "Email ou senha inválidos";
      setErro(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex flex-col lg:flex-row items-center justify-center p-4 lg:p-8">
      <div className="w-full max-w-6xl flex flex-col lg:grid lg:grid-cols-2 gap-8 items-center">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="hidden lg:block"
        >
          <div className="text-center space-y-6">
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className="text-6xl font-bold text-emerald-800 mb-4">
                Green<span className="text-emerald-600">Club</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-md mx-auto">
                Sua plataforma de confiança para cannabis medicinal
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="w-64 h-64 mx-auto bg-gradient-to-br from-emerald-200 to-teal-300 rounded-full flex items-center justify-center"
            >
              <div className="text-8xl">🌿</div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md mx-auto order-first lg:order-last"
        >
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
                {portal === "paciente"
                  ? "Portal do Paciente"
                  : "Backoffice GreenClub"}
              </div>
              <h2 className="mt-4 text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                Entrar
              </h2>
              <p className="text-sm md:text-base text-gray-600">
                {portal === "paciente"
                  ? "Acesse sua jornada de bem-estar com cannabis medicinal."
                  : "Gerencie operações, cadastros e inteligência do GreenClub."}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="seu@email.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={mostrarSenha ? "text" : "password"}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className="w-full pl-12 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {mostrarSenha ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              {erro && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm"
                >
                  {erro}
                </motion.div>
              )}

              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Entrar
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </form>
            <div className="mt-6 space-y-6">
              {/* Oculta o botão de troca de portal se ele for definido pela variável de ambiente */}
              {!import.meta.env.VITE_APP_PORTAL && (
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div /> {/* Placeholder para manter o justify-between */}
                  <motion.a
                    href="?forgot-password=true"
                    whileHover={{ scale: 1.02 }}
                    className="font-medium text-emerald-600 hover:text-emerald-700"
                  >
                    Esqueceu a senha?
                  </motion.a>
                </div>
              )}

              {portal === "paciente" && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4 text-center">
                    Ainda não tem conta?
                  </h3>
                  <motion.a
                    href="?register=paciente"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-teal-50 hover:bg-teal-100 text-teal-700 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2 border border-teal-200"
                  >
                    <User className="w-4 h-4" />
                    Criar conta de paciente
                  </motion.a>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      <div className="lg:hidden text-center mt-8">
        <h1 className="text-3xl font-bold text-emerald-800">
          Green<span className="text-emerald-600">Club</span>
        </h1>
      </div>
    </div>
  );
};
export default Login;
